"use client";

import { motion } from "framer-motion";
import { FaSearch, FaPalette, FaCode, FaBug, FaRocket, FaTools } from "react-icons/fa";

interface ProcessStep {
  icon: (className: string) => JSX.Element;
  title: string;
  description: string;
  baseColor: string;
}

const processSteps: ProcessStep[] = [
  {
    icon: (className) => <FaSearch size={32} className={className} />,
    title: "Discovery & Planning",
    description: "We start by understanding your vision, goals, and target audience. Thorough planning ensures a solid foundation for your project.",
    baseColor: "indigo"
  },
  {
    icon: (className) => <FaPalette size={32} className={className} />,
    title: "Design & Prototyping",
    description: "Our team creates intuitive UI/UX designs and interactive prototypes, focusing on user experience and visual appeal.",
    baseColor: "pink"
  },
  {
    icon: (className) => <FaCode size={32} className={className} />,
    title: "Development",
    description: "Skilled developers bring the designs to life using cutting-edge technologies and best coding practices for robust solutions.",
    baseColor: "green"
  },
  {
    icon: (className) => <FaBug size={32} className={className} />,
    title: "Testing & QA",
    description: "Rigorous testing across devices and scenarios ensures a bug-free, high-performance, and reliable product.",
    baseColor: "yellow"
  },
  {
    icon: (className) => <FaRocket size={32} className={className} />,
    title: "Deployment",
    description: "We handle the seamless deployment of your application to the live environment, ensuring a smooth launch.",
    baseColor: "sky"
  },
  {
    icon: (className) => <FaTools size={32} className={className} />,
    title: "Maintenance & Support",
    description: "Post-launch, we offer ongoing support and maintenance to keep your application running smoothly and up-to-date.",
    baseColor: "purple"
  }
];

// Helper to generate Tailwind color classes
const getColorClasses = (baseColor: string) => {
  // Ensure Tailwind can find these classes. If using JIT, this should be fine.
  // Otherwise, you might need to safelist them in your tailwind.config.js
  return {
    iconColor: `text-${baseColor}-600`,
    iconBg: `bg-${baseColor}-100`,
    borderColor: `border-${baseColor}-500`,
    hoverText: `group-hover:text-${baseColor}-700`,
    timelineDotBg: `bg-${baseColor}-500`,
    stepNumberText: `text-${baseColor}-700`,
    stepNumberBg: `bg-${baseColor}-200`,
  };
};


const fadeInAnimation = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export default function DevelopmentProcess() {
  return (
    <section className="py-20 md:py-28 bg-gradient-to-br from-slate-50 via-gray-100 to-stone-50 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden opacity-5">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="smallGrid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(200,200,220,0.5)" strokeWidth="0.5"/></pattern></defs><rect width="100%" height="100%" fill="url(#smallGrid)" /></svg>
      </div>
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          {...fadeInAnimation}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-24"
        >
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "120px" }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2, ease: "circOut" }}
            className="h-1.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 mx-auto mb-6 md:mb-8 rounded-full"
          />
          <motion.h2
            className="text-4xl sm:text-5xl md:text-6xl font-bold mb-5 md:mb-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
          >
            Our Streamlined Process
          </motion.h2>
          <motion.p
            className="text-gray-600 max-w-3xl mx-auto text-lg md:text-xl leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
          >
            We follow a structured and agile methodology, ensuring transparency and excellence at every stage of your project.
          </motion.p>
        </motion.div>

        <div className="relative">
          {/* Central timeline line for md+ screens - ANIMATED */}
          <motion.div
            className="hidden md:block absolute top-12 bottom-12 left-1/2 w-1.5 bg-gray-300 rounded-full transform -translate-x-1/2" // Changed lg:block to md:block
            initial={{ scaleY: 0, originY: 0 }}
            whileInView={{ scaleY: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: "circOut" }}
            viewport={{ once: true }}
          />

          {/* Removed md:grid-cols-2 from here, lg:grid-cols-1 was effectively making it md:grid-cols-1 for the timeline items */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 md:gap-y-12">
            {processSteps.map((step, index) => {
              const colors = getColorClasses(step.baseColor);
              const isEven = index % 2 === 0;
              const stepNumber = (index + 1).toString().padStart(2, '0');

              return (
                <motion.div
                  key={step.title}
                  className={`md:flex md:justify-${isEven ? 'start' : 'end'} items-start group relative`}
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.2 + 0.2, ease: "circOut" }}
                >
                  <div>
                    {/* Timeline Dot with Step Number for md+ screens */}
                    <div className={`hidden md:flex absolute top-10 ${isEven ? 'left-12' : 'right-0'} transform -translate-x-1/2 -translate-y-1/2 
                                     w-12 h-12 rounded-full ${colors.timelineDotBg} border-[5px] border-slate-50 shadow-xl 
                                     items-center justify-center font-bold text-white text-base z-10`}>
                       {stepNumber}
                    </div>

                    {/* Content Block */}
                    <div className="relative">
                       {/* Step Number Badge for sm screens (hidden on md+) */}
                      <div className={`absolute -top-2 right-4
                                       w-12 h-12 rounded-full ${colors.stepNumberBg} ${colors.stepNumberText} 
                                       flex items-center justify-center text-lg font-bold shadow-md md:hidden z-20`}>
                          {stepNumber}
                      </div>

                      <motion.div
                        className={`bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 ease-out 
                                    border-t-4 ${colors.borderColor} p-6 pt-10 md:p-8 md:pt-10 h-full flex flex-col 
                                    transform hover:-translate-y-2.5`}
                        whileHover={{ scale: 1.035 }}
                      >
                        {/* Icon and title order alternates from md screens */}
                        <div className={`flex items-center gap-4 mb-4 ${isEven ? 'md:flex-row-reverse' : ''}`}>
                          <div className={`flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-lg ${colors.iconBg} 
                                          flex items-center justify-center shadow-md transition-all duration-300 
                                          group-hover:scale-110 group-hover:shadow-lg ${colors.iconColor}`}>
                            {step.icon(colors.iconColor)}
                          </div>
                          <h3 className={`text-xl md:text-2xl font-semibold text-gray-800 ${colors.hoverText} transition-colors duration-300`}>
                            {step.title}
                          </h3>
                        </div>
                        <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                          {step.description}
                        </p>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}